import {
  BooleanField,
  Button,
  Identifier,
  TextField,
  useDataProvider,
  useRecordContext,
} from "react-admin";

import { useModalState, useMutation } from "../../hooks";
import {
  Action,
  CustomDataProvider,
  DataKind,
  Resource,
  SsoInfoResponse,
} from "../../types";
import {
  Authorize,
  ConfirmationModal,
  CustomDateTimeField,
  CustomReferenceFieldLazy,
  CustomShow,
  ShowTable,
} from "../custom";

function DisableSsoInfo() {
  const record = useRecordContext<SsoInfoResponse>();

  if (record?.is_enabled) {
    return <DisableSsoInfoButton ssoInfoRecord={record} />;
  } else {
    return null;
  }
}

function DisableSsoInfoButton(props: { ssoInfoRecord: SsoInfoResponse }) {
  const { handleClose, handleOpen, modalOpen } = useModalState();
  const dataProvider = useDataProvider<CustomDataProvider>();

  const { mutate, isLoading } = useMutation(
    (ssoInfoId: Identifier) => dataProvider.disableSsoInfo(ssoInfoId),
    ["disableSsoInfo", { ssoInfoId: props.ssoInfoRecord.id }],
    "Disabled SSO info successfully! ✅",
  );

  const handleDisableSsoInfo = () => {
    handleClose();
    mutate(props.ssoInfoRecord.id);
  };

  return (
    <>
      <Button
        disabled={isLoading}
        label="Disable"
        onClick={handleOpen}
        variant="fieldwire-danger"
      />
      <ConfirmationModal
        cancelHandler={handleClose}
        confirmHandler={handleDisableSsoInfo}
        confirmButtonText="Disable"
        confirmButtonVariant="fieldwire-danger"
        description={`Alert: Proceeding will disable SSO for ${props.ssoInfoRecord.domain}. Users of this domain will no longer be routed through SSO.`}
        modalOpen={modalOpen}
        title="Disable SSO"
      />
    </>
  );
}

function EnableSsoInfo() {
  const record = useRecordContext<SsoInfoResponse>();

  if (record && !record.is_enabled) {
    return <EnableSsoInfoButton ssoInfoRecord={record} />;
  } else {
    return null;
  }
}

function EnableSsoInfoButton(props: { ssoInfoRecord: SsoInfoResponse }) {
  const { handleClose, handleOpen, modalOpen } = useModalState();
  const dataProvider = useDataProvider<CustomDataProvider>();

  const { mutate, isLoading } = useMutation(
    (ssoInfoId: Identifier) => dataProvider.enableSsoInfo(ssoInfoId),
    ["enableSsoInfo", { ssoInfoId: props.ssoInfoRecord.id }],
    "Enabled SSO info successfully! ✅",
  );

  const handleEnableSsoInfo = () => {
    handleClose();
    mutate(props.ssoInfoRecord.id);
  };

  return (
    <>
      <Button
        disabled={isLoading}
        label="Enable"
        onClick={handleOpen}
        variant="fieldwire-primary"
      />
      <ConfirmationModal
        cancelHandler={handleClose}
        confirmHandler={handleEnableSsoInfo}
        confirmButtonText="Enable"
        confirmButtonVariant="fieldwire-primary"
        description={`Alert: Proceeding will enable SSO for ${props.ssoInfoRecord.domain}. Users of this domain will be required to sign in through SSO.`}
        modalOpen={modalOpen}
        title="Enable SSO"
      />
    </>
  );
}

function SsoInfoShow() {
  return (
    <CustomShow
      checkPermissionFor={DataKind.SsoInfo}
      displayDelete={true}
      displayEdit={true}
      fieldwireActions={
        <>
          <Authorize
            action={Action.Create}
            dataKind={DataKind.SsoInfoEnablement}
            disableUnauthorizedMessage
          >
            <EnableSsoInfo />
          </Authorize>
          <Authorize
            action={Action.Delete}
            dataKind={DataKind.SsoInfoEnablement}
            disableUnauthorizedMessage
          >
            <DisableSsoInfo />
          </Authorize>
        </>
      }
    >
      <ShowTable title="Sso Info Details">
        <TextField label="Id" source="id" />
        <TextField label="Domain" source="domain" />
        <TextField
          label="Auth0 Connection Name"
          source="auth0_connection_name"
        />
        <BooleanField label="Is Enabled" source="is_enabled" />
        <TextField
          label="Notes"
          source="notes"
          sx={{ whiteSpace: "pre-wrap" }}
        />
        <CustomDateTimeField label="Created At" source="created_at" />
        <CustomDateTimeField label="Updated At" source="updated_at" />
        <CustomReferenceFieldLazy
          label="Account"
          source="account_id"
          reference={Resource.Account}
        />
      </ShowTable>
    </CustomShow>
  );
}

export default SsoInfoShow;
